import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Upload, CheckCircle2, XCircle, Clock, AlertCircle, RefreshCw, FileText, ArrowUpFromLine, Download } from 'lucide-react';
import RowEditor from './RowEditor';

/**
 * Requirements panel shown above a KindPanel — used today only for 'rates' so
 * admins see the schema + can grab a template before they upload the wrong file.
 */
export function RatesRequirementsPanel() {
  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/admin/upload/rates/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rates-upload-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Template download failed');
    }
  };

  return (
    <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-5 space-y-3" data-testid="rates-requirements-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-dash-ink">Upload rules for Grade Strategy / Price Trend</h3>
        <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-rates-template">
          <Download className="w-4 h-4 mr-2" /> Download template (.xlsx)
        </Button>
      </div>
      <ul className="text-sm text-dash-ink/90 space-y-1.5 list-disc pl-5">
        <li>File must be <strong>.xlsx</strong> or a JSON array — nothing else is accepted.</li>
        <li>Exactly these <strong>5 columns</strong>, case-insensitive, no extras: <code>date</code>, <code>count</code>, <code>sil</code>, <code>aqua</code>, <code>skm</code>.</li>
        <li><strong>date</strong>: <code>yyyy-mm-dd</code> (a real Excel date works too). Wrong format → whole file rejected.</li>
        <li><strong>count</strong>: grade / size label, e.g. <code>28-30</code>, <code>40</code>, <code>110/120</code>. Never seen before? You get a soft warning but can still publish.</li>
        <li><strong>sil / aqua / skm</strong>: numeric rates. Leave blank when a supplier didn't quote.</li>
        <li>Upload only the <strong>new</strong> dates — history is kept. If a (date, grade) pair already exists, the file is rejected outright.</li>
        <li>No duplicate rows inside the file, no empty file.</li>
      </ul>
    </div>
  );
}

export function RussiaRegisterRequirementsPanel() {
  return (
    <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-5 space-y-3" data-testid="russia-register-requirements-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-dash-ink">Upload rules for Russian Federation Website Data</h3>
      </div>
      <ul className="text-sm text-dash-ink/90 space-y-1.5 list-disc pl-5">
        <li>File must be <strong>.xlsx</strong> or <strong>.xls</strong>, with a sheet named exactly <strong>Enterprises</strong>.</li>
        <li>Required columns: <code>No.</code>, <code>Reg. No.</code>, <code>Company Name</code>, <code>Address</code>, <code>Certified Product / Category</code>, then repeating <code>Colour &lt;date&gt;</code> / <code>Since &lt;date&gt;</code> / <code>Samples &lt;date&gt;</code> groups — any number of date-groups is fine.</li>
        <li>The workbook already carries its own running day-by-day history in those column groups, so <strong>every publish fully replaces the register</strong> — it doesn't merge with what's currently live.</li>
        <li>Each upload is archived (never auto-deleted), so a bad file can always be rolled back by re-publishing an older copy.</li>
        <li>Maximum file size: <strong>25 MB</strong>.</li>
      </ul>
    </div>
  );
}

/**
 * Requirements panel shown above the general-stores KindPanel — tells admins what
 * the MRO Stock Summary must look like and how same-date re-uploads behave.
 */
export function GeneralStoresRequirementsPanel() {
  return (
    <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-5 space-y-3" data-testid="general-stores-requirements-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-dash-ink">Upload rules for General Stores (MRO)</h3>
      </div>
      <ul className="text-sm text-dash-ink/90 space-y-1.5 list-disc pl-5">
        <li>File must be <strong>.xlsx</strong> or <strong>.xls</strong> — the MRO Stock Summary export from your ERP.</li>
        <li>The <strong>"To" date is read from row 6 of the sheet</strong>, not from the filename — filename can be anything.</li>
        <li>Row 8 onwards is one line item per row. Row 3's totals row is used only to sanity-check the parse.</li>
        <li>Opening / Issue / Closing Values in the file are <strong>discarded and recomputed on a FIFO basis</strong>; Item code, name, quantities and Receipt Value are trusted verbatim.</li>
        <li>Re-uploading the <strong>same date auto-replaces</strong> it — the previous day's file for that date is overwritten and the whole dataset is rebuilt.</li>
        <li>Backdated uploads are fine — the FIFO simulation runs chronologically across every currently-active date, so inserting an older day recomputes forward from there.</li>
        <li>Maximum file size: <strong>25 MB</strong>.</li>
      </ul>
      <p className="text-xs text-dash-mut pt-1">
        Same underlying pipeline as the Upload panel on the General Stores dashboard — stores users can upload from either place.
      </p>
    </div>
  );
}



const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KIND_META = {
  'invoice-pi':    { label: 'Invoice & PI Tracker', accept: '.json,.xlsx', hint: 'JSON matching invoice_pi.json shape, or Excel with the same columns.', hasRows: true },
  'solar-project': { label: 'Solar Project',        accept: '.xlsx',        hint: 'Excel with 2 header rows (Invoice details.xlsx layout).', hasRows: true },
  'rates':         { label: 'Grade Strategy / Price Trend', accept: '.xlsx,.json', hint: 'Excel/JSON with exactly these columns: date, count, sil, aqua, skm. Adds new rows only — upload just the new dates, not the full history. Wrong format or a row that duplicates existing data gets rejected with the reason.' },
  'russia-register': { label: 'Russian Federation Website Data', accept: '.xlsx,.xls', hint: 'Enterprises workbook (crustacean export register). Every publish fully replaces the register — the workbook already carries its own day-by-day Colour/Since/Samples history.' },
  'exports':       { label: 'Vannamei Exports (231k rows)', accept: '.xlsx,.csv,.numbers,.json,.parquet', hint: 'Use the standalone Files → Upload page (Vannamei monthly exports append to files table).', special: true },
};

function StatusBadge({ status }) {
  const map = {
    pending:   { icon: Clock,        cls: 'bg-yellow-500/15 text-yellow-500 border-yellow-500/30', label: 'Pending' },
    published: { icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30', label: 'Published' },
    cancelled: { icon: XCircle,      cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30', label: 'Cancelled' },
    failed:    { icon: AlertCircle,  cls: 'bg-red-500/15 text-red-500 border-red-500/30', label: 'Failed' },
  };
  const { icon: Icon, cls, label } = map[status] || map.pending;
  return <Badge variant="outline" className={cls}><Icon className="w-3 h-3 mr-1" />{label}</Badge>;
}

export function KindPanel({ kind, onStagingChange }) {
  const meta = KIND_META[kind];
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { staging_id, summary, preview[], total_rows }
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async () => {
    if (!file) { toast.error('Select a file first'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload/${kind}`, fd, { withCredentials: true });
      setPreview(data);
      onStagingChange?.();
      toast.success(`Parsed ${data.total_rows || 0} rows — review then publish`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    if (!preview?.staging_id) return;
    if (!window.confirm(`Publish ${preview.total_rows || 0} rows to production data? This is live for all users.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/publish/${preview.staging_id}`, {}, { withCredentials: true });
      toast.success(`Published ${data.rows_published} rows`);
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onStagingChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!preview?.staging_id) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/upload/${preview.staging_id}`, { withCredentials: true });
      toast('Draft discarded');
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onStagingChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  if (meta.special) {
    return (
      <div className="rounded-lg border border-dash-line bg-dash-panel p-6" data-testid={`kind-panel-${kind}`}>
        <p className="text-sm text-dash-mut">{meta.hint}</p>
        <Button className="mt-4" variant="outline" onClick={() => window.location.href = '/upload'} data-testid={`goto-upload-${kind}`}>
          <ArrowUpFromLine className="w-4 h-4 mr-2" /> Open Files → Upload
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-dash-line bg-dash-panel p-6 space-y-4" data-testid={`kind-panel-${kind}`}>
      <p className="text-sm text-dash-mut">{meta.hint}</p>

      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept={meta.accept}
          onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); }}
          disabled={busy || !!preview}
          className="text-sm text-dash-ink file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:bg-dash-panel2 file:text-dash-ink file:cursor-pointer"
          data-testid={`file-input-${kind}`}
        />
        <Button
          onClick={handleUpload}
          disabled={!file || busy || !!preview}
          data-testid={`upload-btn-${kind}`}
        >
          <Upload className="w-4 h-4 mr-2" /> Parse & Preview
        </Button>
      </div>

      {preview && (
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-dash-ink font-medium">{preview.total_rows} rows parsed</span>
            {preview.summary?.errors > 0 && (
              <span className="text-red-500">{preview.summary.errors} errors</span>
            )}
            {preview.summary?.will_replace_table && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                Will REPLACE existing table
              </Badge>
            )}
            {preview.summary?.will_append && (
              <Badge variant="outline" className="bg-sky-500/10 text-sky-500 border-sky-500/30">
                Adds to existing table
              </Badge>
            )}
          </div>

          {(preview.summary?.unknown_grade_warning?.length > 0 || preview.summary?.stale_date_warning?.length > 0) && (
            <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-500 space-y-1" data-testid="rates-warnings">
              {preview.summary?.unknown_grade_warning?.length > 0 && (
                <p>
                  <strong>Unrecognized grade label(s):</strong> {preview.summary.unknown_grade_warning.join(', ')}.
                  Not seen in the existing data before — double-check for a typo. If it's a real new grade
                  bracket, it's safe to publish as-is.
                </p>
              )}
              {preview.summary?.stale_date_warning?.length > 0 && (
                <p>
                  <strong>Older than existing data:</strong> {preview.summary.stale_date_warning.join(', ')}.
                  These dates are before the latest date already stored — make sure this is intentional
                  and not a stray/old row before publishing.
                </p>
              )}
            </div>
          )}

          {preview.preview?.length > 0 && (
            <div className="overflow-auto max-h-64 rounded border border-dash-line">
              <table className="min-w-full text-xs">
                <thead className="bg-dash-panel2 sticky top-0">
                  <tr>
                    {Object.keys(preview.preview[0]).slice(0, 8).map(k => (
                      <th key={k} className="px-3 py-2 text-left text-dash-mut font-normal">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={i} className="border-t border-dash-line/40">
                      {Object.keys(preview.preview[0]).slice(0, 8).map(k => (
                        <td key={k} className="px-3 py-1.5 text-dash-ink truncate max-w-[160px]" title={String(row[k] ?? '')}>
                          {String(row[k] ?? '—').slice(0, 60)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.total_rows > preview.preview.length && (
                <div className="px-3 py-2 text-xs text-dash-mut border-t border-dash-line/40">
                  Showing first {preview.preview.length} of {preview.total_rows} rows.
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={handlePublish} disabled={busy} data-testid={`publish-btn-${kind}`}>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Publish to Live
            </Button>
            <Button onClick={handleCancel} variant="outline" disabled={busy} data-testid={`cancel-btn-${kind}`}>
              Discard Draft
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/uploads?limit=15`, { withCredentials: true });
      setRows(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  return (
    <Card className="bg-dash-panel border-dash-line" data-testid="admin-upload-history">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm text-dash-ink">Recent Uploads</CardTitle>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="refresh-history">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="px-6 pb-6 text-sm text-dash-mut">No uploads yet.</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead className="bg-dash-panel2">
              <tr>
                <th className="px-4 py-2 text-left text-dash-mut font-normal">When</th>
                <th className="px-4 py-2 text-left text-dash-mut font-normal">Kind</th>
                <th className="px-4 py-2 text-left text-dash-mut font-normal">File</th>
                <th className="px-4 py-2 text-left text-dash-mut font-normal">Admin</th>
                <th className="px-4 py-2 text-left text-dash-mut font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-dash-line/40 hover:bg-dash-panel2/40">
                  <td className="px-4 py-2 text-dash-ink whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-dash-ink">{KIND_META[r.kind]?.label || r.kind}</td>
                  <td className="px-4 py-2 text-dash-mut max-w-[240px] truncate" title={r.source_filename}>
                    <FileText className="w-3 h-3 inline mr-1 opacity-60" />
                    {r.source_filename || '—'}
                  </td>
                  <td className="px-4 py-2 text-dash-mut">{r.admin_email || '—'}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminDataOpsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey(k => k + 1);

  return (
    <div className="p-6 space-y-6" data-testid="admin-data-ops-page">
      <div>
        <h1 className="text-2xl font-semibold text-dash-ink">Data Operations</h1>
        <p className="text-sm text-dash-mut mt-1">
          Upload fresh data for any dashboard. Review the preview, then publish to go live for every user.
        </p>
      </div>

      <Tabs defaultValue="invoice-pi" className="space-y-4">
        <TabsList className="bg-dash-panel border border-dash-line" data-testid="admin-tabs">
          {Object.entries(KIND_META).map(([kind, meta]) => (
            <TabsTrigger key={kind} value={kind} data-testid={`tab-${kind}`}>{meta.label}</TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(KIND_META).map(kind => (
          <TabsContent key={kind} value={kind}>
            {KIND_META[kind].hasRows ? (
              <Tabs defaultValue="upload" className="space-y-4">
                <TabsList className="bg-dash-panel border border-dash-line">
                  <TabsTrigger value="upload" data-testid={`sub-tab-upload-${kind}`}>Bulk Upload</TabsTrigger>
                  <TabsTrigger value="rows" data-testid={`sub-tab-rows-${kind}`}>Manage Rows</TabsTrigger>
                </TabsList>
                <TabsContent value="upload">
                  <KindPanel kind={kind} onStagingChange={bump} />
                </TabsContent>
                <TabsContent value="rows">
                  <RowEditor kind={kind} onChange={bump} />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="space-y-4">
                {kind === 'rates' && <RatesRequirementsPanel />}
                {kind === 'russia-register' && <RussiaRegisterRequirementsPanel />}
                {kind === 'general-stores' && <GeneralStoresRequirementsPanel />}
                <KindPanel kind={kind} onStagingChange={bump} />
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <HistoryPanel refreshKey={refreshKey} />
    </div>
  );
}
